import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IMailrailDripStep, MailrailDripEmailCampaign } from '@feathr/blackbox';
import { MailrailDripStep } from '@feathr/blackbox';
import { Button, CardV2 as Card, Label } from '@feathr/components';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary';
import { useStore } from '@feathr/extender/state';

import EmailTemplateConfig from '../EmailTemplateConfig';
import MailrailMergeDataInputs from '../MailrailMergeDataInputs';
import SenderInformationConfig from '../SenderInformationConfig';
import { useEphemeralMailing } from '../useEphemeralMailing';
import DripStepTargeting from './DripStepTargeting';

// Each synced edit re-validates every card and re-renders the builder, so sync at most this often.
const STEP_SYNC_DEBOUNCE_WAIT = 250;

interface IDripStepCardEditorProps {
  step: IMailrailDripStep;
  campaign: MailrailDripEmailCampaign;
  validationErrors: ICampaignValidationErrors;
  disabled?: boolean;
  // The step's changed fields (dirty patch), emitted on each edit.
  onChange: (patch: Partial<IMailrailDripStep>) => void;
  // Collapse the card back to preview.
  onDone: () => void;
}

/**
 * The editor for an embedded drip step (cards 1..N). It binds the step-targeting selector
 * and the shared mailing config to an ephemeral copy of the step via {@link useEphemeralMailing},
 * which edits live: each change emits the changed-fields patch through `onChange` so the
 * coordinator merges it into `campaign.steps`. The original `step` passed in is never mutated
 * directly. A single Done button returns the card to preview mode.
 *
 * @param props - {@link IDripStepCardEditorProps}.
 */
function DripStepCardEditor({
  step,
  campaign,
  validationErrors,
  disabled = false,
  onChange,
  onDone,
}: Readonly<IDripStepCardEditorProps>): JSX.Element {
  const { t } = useTranslation();
  const { Templates } = useStore();
  const ephemeralStep = useEphemeralMailing(MailrailDripStep, step, step.step_id, onChange, {
    delay: STEP_SYNC_DEBOUNCE_WAIT,
  });
  const templateId = ephemeralStep.get('template_id');
  const template = templateId ? Templates.get(templateId) : undefined;

  return (
    <Card theme={'disabled'} width={'full'}>
      <Card.Content>
        <Label weight={'medium'}>{t('Automation')}</Label>
        <DripStepTargeting
          disabled={disabled}
          mailing={ephemeralStep}
          validationErrors={validationErrors}
        />
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Sender Information')}</Label>
        <SenderInformationConfig
          disabled={disabled}
          mailing={ephemeralStep}
          validationErrors={validationErrors}
          variant={'well'}
        />
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Email Template')}</Label>
        <EmailTemplateConfig
          campaign={campaign}
          disabled={disabled}
          mailing={ephemeralStep}
          template={template}
          validationErrors={validationErrors}
          variant={'well'}
        />
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Default Merge Data')}</Label>
        <MailrailMergeDataInputs disabled={disabled} template={template} />
      </Card.Content>
      {!disabled && (
        <Card.Actions>
          <Button name={'done-step'} onClick={onDone} type={'primary'}>
            {t('Done')}
          </Button>
        </Card.Actions>
      )}
    </Card>
  );
}

export default observer(DripStepCardEditor);
